import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { initializeApp } from 'firebase/app';
import {
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  getIdToken,
  getMultiFactorResolver,
  multiFactor,
  TotpMultiFactorGenerator,
  sendPasswordResetEmail,
  sendEmailVerification
} from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
export const db = getFirestore(app);
const AuthContext = createContext();

// Where the Decode platform lives for this build of the Site.
const DECODE_ORIGIN =
  process.env.REACT_APP_FIREBASE_PROJECT_ID === 'staging-site-432914'
    ? 'http://localhost:3000'
    : 'https://insurance.bimanalytics.ai';
const TOKEN_ENDPOINT = `https://europe-west2-${process.env.REACT_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net/get_custom_tokens`;
// How long to wait for get_custom_tokens before giving up.
const TOKEN_FETCH_TIMEOUT_MS = 20000;
// How long to keep offering the tokens to the Decode tab. The tab cannot
// receive anything until its bundle has downloaded, which on a slow connection
// can take well over half a minute; resending is harmless.
const TOKEN_DELIVERY_CAP_MS = 120000;
const TOKEN_DELIVERY_INTERVAL_MS = 250;

// An error in the Site -> Decode launch that carries a message fit for the user.
class PlatformLaunchError extends Error {
  constructor(message, userMessage) {
    super(message);
    this.name = 'PlatformLaunchError';
    this.userMessage = userMessage;
  }
}

// Posts the tokens to the Decode tab until Decode reports the outcome of
// signing in with them, the tab closes, or the cap elapses. An onload handler
// set on a cross-origin window never fires, so polling is the only way to
// know when the tab is ready. Decode acknowledges only once both of its
// sign-ins have succeeded, and reports a failure otherwise. Resolves with
// 'acknowledged' or 'closed'; rejects on a failure or on the cap.
function deliverTokens(targetWindow, message) {
  return new Promise((resolve, reject) => {
    let retryTimer = null;
    let capTimer = null;
    const stop = () => {
      clearInterval(retryTimer);
      clearTimeout(capTimer);
      window.removeEventListener('message', receiveReply);
    };
    const receiveReply = (event) => {
      if (event.origin !== DECODE_ORIGIN) return;
      if (event.source !== targetWindow) return;
      const type = event.data && event.data.type;
      if (type === 'decode-bridge-ack') {
        stop();
        resolve('acknowledged');
      } else if (type === 'decode-bridge-failed') {
        stop();
        reject(new PlatformLaunchError(
          'Decode could not sign in with the tokens',
          'Decode could not complete your sign-in. Close the Decode tab and try again.'
        ));
      }
    };
    const send = () => {
      if (targetWindow.closed) {
        stop();
        resolve('closed');
        return;
      }
      targetWindow.postMessage(message, DECODE_ORIGIN);
    };
    window.addEventListener('message', receiveReply, false);
    capTimer = setTimeout(() => {
      stop();
      reject(new PlatformLaunchError(
        'Decode did not acknowledge the tokens',
        'Decode did not confirm your sign-in. If the Decode tab is still loading, give it a moment; otherwise close it and try again.'
      ));
    }, TOKEN_DELIVERY_CAP_MS);
    send();
    retryTimer = setInterval(send, TOKEN_DELIVERY_INTERVAL_MS);
  });
}

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userClaims, setUserClaims] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionError, setSessionError] = useState(null);
  const sessionGeneration = useRef(0);
  const [mfaResolver, setMfaResolver] = useState(null);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaEnrolled, setMfaEnrolled] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  // Progress of the "open Decode" action: idle | opening | error. Read by the
  // Header to disable the button while a launch is in flight and to surface
  // failures, which previously went only to the console.
  const [platformLaunch, setPlatformLaunch] = useState({ status: 'idle', error: null });
  // Ref rather than state so a second click in the same tick is refused.
  const platformLaunchRef = useRef(false);

  async function login(email, password) {
    try {
      await signInWithEmailAndPassword(auth, email, password);
      setMfaResolver(null);
      setMfaRequired(false);
      return { mfaRequired: false };
    } catch (error) {
      if (error.code === 'auth/multi-factor-auth-required') {
        const resolver = getMultiFactorResolver(auth, error);
        setMfaResolver(resolver);
        setMfaRequired(true);
        return { mfaRequired: true };
      }
      throw error;
    }
  }

  async function completeMfaSignIn(otpCode) {
    if (!mfaResolver) throw new Error('No MFA resolver available');
    const totpHint = mfaResolver.hints.find(h => h.factorId === 'totp');
    if (!totpHint) throw new Error('No TOTP factor enrolled');
    const assertion = TotpMultiFactorGenerator.assertionForSignIn(totpHint.uid, otpCode);
    try {
      await mfaResolver.resolveSignIn(assertion);
    } catch (error) {
      const restartCodes = [
        'auth/invalid-multi-factor-session', 'auth/missing-multi-factor-session',
        'auth/multi-factor-info-not-found', 'auth/invalid-credential',
        'auth/code-expired', 'auth/user-token-expired', 'auth/user-disabled',
        'auth/user-not-found',
      ];
      if (!restartCodes.includes(error.code)) throw error;
      await logout();
      const restart = new Error('This sign-in challenge is no longer valid. Please sign in again.', { cause: error });
      restart.code = 'auth/restart-sign-in';
      throw restart;
    }
    setMfaResolver(null);
    setMfaRequired(false);
  }

  async function getMfaEnrollmentStatus() {
    // Use auth.currentUser directly to avoid race with React state updates.
    // Reload from server to guarantee we read authoritative mfaInfo rather
    // than a stale SDK cache.
    const user = auth.currentUser;
    if (!user) return false;
    await user.reload();
    const enrolled = multiFactor(user).enrolledFactors.length > 0;
    setMfaEnrolled(enrolled);
    return enrolled;
  }

  async function startMfaEnrollment() {
    if (!currentUser) throw new Error('User not authenticated');
    const session = await multiFactor(currentUser).getSession();
    const totpSecret = await TotpMultiFactorGenerator.generateSecret(session);
    return totpSecret;
  }

  async function finalizeMfaEnrollment(totpSecret, verificationCode) {
    const assertion = TotpMultiFactorGenerator.assertionForEnrollment(totpSecret, verificationCode);
    await multiFactor(currentUser).enroll(assertion, 'Authenticator App');
    setMfaEnrolled(true);
  }

  async function unenrollMfa() {
    if (!currentUser) throw new Error('User not authenticated');
    const enrolledFactors = multiFactor(currentUser).enrolledFactors;
    const totpFactor = enrolledFactors.find(f => f.factorId === 'totp');
    if (!totpFactor) throw new Error('No TOTP factor enrolled');
    await multiFactor(currentUser).unenroll(totpFactor);
    setMfaEnrolled(multiFactor(currentUser).enrolledFactors.length > 0);
  }

  async function sendVerificationEmail() {
    const user = auth.currentUser;
    if (!user) throw new Error('Sign in before requesting a verification email.');
    await sendEmailVerification(user, { url: `${window.location.origin}/verify-email` });
  }

  async function resetPassword(email) {
    await sendPasswordResetEmail(auth, email);
  }

  async function logout() {
    setMfaResolver(null);
    setMfaRequired(false);
    await signOut(auth);
  }

  async function getAuthToken() {
    if (currentUser) {
      return await getIdToken(currentUser, true);
    }
    return null;
  }

  // Requests the Decode custom tokens for the signed-in user. Every failure is
  // thrown as a PlatformLaunchError carrying a message for the user.
  async function fetchPlatformTokens() {
    let idToken;
    try {
      idToken = await getAuthToken();
    } catch (error) {
      throw new PlatformLaunchError(
        `token refresh failed: ${error.message}`,
        'Could not refresh your session. Check your connection and try again.'
      );
    }
    if (!idToken) {
      throw new PlatformLaunchError('no signed-in user', 'Your session has expired. Please log in again.');
    }

    const controller = new AbortController();
    const abortTimer = setTimeout(() => controller.abort(), TOKEN_FETCH_TIMEOUT_MS);
    let response;
    try {
      response = await fetch(TOKEN_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idToken }),
        signal: controller.signal
      });
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new PlatformLaunchError(
          'get_custom_tokens timed out',
          'Decode took too long to respond. Check your connection and try again.'
        );
      }
      throw new PlatformLaunchError(
        `get_custom_tokens unreachable: ${error.message}`,
        'Could not reach Decode. Check your connection and try again.'
      );
    } finally {
      clearTimeout(abortTimer);
    }

    let data = {};
    try {
      data = await response.json();
    } catch (error) {
      data = {};
    }
    if (response.status === 403) {
      throw new PlatformLaunchError(
        `get_custom_tokens refused: ${data.error}`,
        data.error === 'Email verification required'
          ? 'Verify your email address before opening Decode.'
          : data.error === 'MFA enrollment required'
            ? 'Set up two-factor authentication before opening Decode.'
          : 'Your account does not have access to Decode.'
      );
    }
    if (!response.ok || !data.mainCustomToken || !data.platformCustomToken) {
      throw new PlatformLaunchError(
        `get_custom_tokens failed: ${data.error || `HTTP ${response.status}`}`,
        'Decode sign-in failed. Please try again in a moment.'
      );
    }
    return { mainCustomToken: data.mainCustomToken, platformCustomToken: data.platformCustomToken };
  }

  function navigateToPlatform() {
    if (loading || sessionError) {
      setPlatformLaunch({
        status: 'error',
        error: 'Retry session verification before opening Decode.'
      });
      return;
    }
    if (!currentUser || !emailVerified || !mfaEnrolled) {
      const error = !currentUser ? 'Sign in before opening Decode.'
        : !emailVerified ? 'Verify your email address before opening Decode.'
          : 'Set up two-factor authentication before opening Decode.';
      setPlatformLaunch({ status: 'error', error });
      return;
    }
    if (platformLaunchRef.current) return;

    // Open the tab synchronously, inside the click's user-activation window.
    // Browsers block window.open once roughly five seconds have passed since
    // the click, and the token request can take longer than that on a cold
    // instance or a slow connection. Decode shows "Signing you in" while it
    // waits, and downloads its bundle in parallel with the token minting.
    const platformWindow = window.open(DECODE_ORIGIN, '_blank');
    if (!platformWindow) {
      setPlatformLaunch({
        status: 'error',
        error: 'Your browser blocked the Decode tab. Allow pop-ups for this site and try again.'
      });
      return;
    }

    platformLaunchRef.current = true;
    setPlatformLaunch({ status: 'opening', error: null });

    (async () => {
      let tokens;
      try {
        tokens = await fetchPlatformTokens();
      } catch (error) {
        console.error('Error fetching Decode tokens:', error);
        // Nothing can sign that tab in now, so close it rather than leave it
        // sitting on "Signing you in".
        if (!platformWindow.closed) platformWindow.close();
        platformLaunchRef.current = false;
        setPlatformLaunch({
          status: 'error',
          error: error.userMessage || 'Could not open Decode. Please try again.'
        });
        return;
      }

      try {
        await deliverTokens(platformWindow, tokens);
        setPlatformLaunch({ status: 'idle', error: null });
      } catch (error) {
        console.error('Error delivering Decode tokens:', error);
        setPlatformLaunch({
          status: 'error',
          error: error.userMessage || 'Could not open Decode. Please try again.'
        });
      } finally {
        platformLaunchRef.current = false;
      }
    })();
  }

  function clearPlatformLaunchError() {
    setPlatformLaunch((current) =>
      current.status === 'error' ? { status: 'idle', error: null } : current
    );
  }

  function checkPermission(permission) {
    return userClaims && userClaims.permissions && userClaims.permissions.includes(permission);
  }

  function checkRole(role) {
    return userClaims && userClaims.role === role;
  }

  const refreshSession = useCallback(async ({ keepPage = false } = {}) => {
    const user = auth.currentUser;
    const generation = ++sessionGeneration.current;
    if (!keepPage) setLoading(true);
    if (!user) {
      setMfaResolver(null);
      setMfaRequired(false);
      setMfaEnrolled(false);
      setEmailVerified(false);
      setUserClaims(null);
      setCurrentUser(null);
      setSessionError(null);
      setLoading(false);
      return null;
    }
    try {
      await user.reload();
      if (generation !== sessionGeneration.current || auth.currentUser !== user) return null;
      const token = await user.getIdTokenResult(true);
      if (generation !== sessionGeneration.current || auth.currentUser !== user) return null;
      const enrolled = multiFactor(user).enrolledFactors.length > 0;
      const verified = user.emailVerified === true;
      setMfaEnrolled(enrolled);
      setEmailVerified(verified);
      setUserClaims(token.claims);
      setCurrentUser(user);
      setSessionError(null);
      setLoading(false);
      return { mfaEnrolled: enrolled, emailVerified: verified };
    } catch (error) {
      if (generation !== sessionGeneration.current || auth.currentUser !== user) return null;
      console.error('Could not verify the signed-in session:', error);
      setUserClaims(null);
      setCurrentUser(user);
      setSessionError('You are signed in, but we could not finish verifying your session. Check your connection and try again.');
      setLoading(false);
      return null;
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user === auth.currentUser) refreshSession();
    });
    return () => {
      sessionGeneration.current += 1;
      unsubscribe();
    };
  }, [refreshSession]);

  const value = {
    currentUser,
    userClaims,
    loading,
    sessionError,
    verifySession: refreshSession,
    platformLaunch,
    clearPlatformLaunchError,
    login,
    logout,
    getAuthToken,
    navigateToPlatform,
    checkPermission,
    checkRole,
    mfaRequired,
    mfaEnrolled,
    emailVerified,
    sendVerificationEmail,
    completeMfaSignIn,
    getMfaEnrollmentStatus,
    startMfaEnrollment,
    finalizeMfaEnrollment,
    unenrollMfa,
    resetPassword
  };

  // Children render immediately so the public site never waits on Firebase.
  // Anything that needs a resolved session (PrivateRoute, the Header's account
  // controls, the login form) reads `loading` and holds off until it clears.
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
